import { Formik, Form as FormikForm, FormikValues, Field } from "formik";
import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setLoading } from "../../../../store/slices/loaderSlice";
import * as yup from "yup";
import {
  confimationStyles,
  errorStyles,
} from "../../../../assets/styles/toast";
import Error from "../../../atoms/Error";
import { postData } from "../../../../services";
import { expirationTime } from "../../../../helpers/expirationTime";
import TextInput from "../../../atoms/TextInput";
import Mail from "../../../icons/Mail";
import { fetchAccessData } from "../../../../store/slices/accessSlice";

const Form = () => {
  const navigate = useNavigate(-1);
  const [resendLoading, setResendLoading] = useState(false);
  const dispatch = useDispatch();
  const [cookie, setCookie] = useCookies([
    "euid",
    "role",
    "eun",
    "eut",
    "e_t",
    "eid",
    "profile",
  ]);

  const storedCountdownStartTime = localStorage.getItem("countdownStartTime");
  const initialResendTimer = storedCountdownStartTime
    ? Number(storedCountdownStartTime)
    : 30;

  const [resendTimer, setResendTimer] = useState(30);
  const [showResend, setShowResend] = useState(false);

  const startResendTimer = () => {
    setResendTimer(30);
    setShowResend(false);
  };

  const handleResendCompleted = () => {
    startResendTimer();
  };

  const initialValues = {
    otp: "",
  };

  const validationSchema = yup.object().shape({
    otp: yup
      .string()
      .length(6, "OTP is 6 digits long")
      .required("OTP is required"),
  });

  const handleResendClick = async () => {
    try {
      setResendLoading(true);
      dispatch(setLoading(true));
      const { data: resultData } = await postData({
        endpoint: "Auth/resendOtp",
        data: {},
        params: { user_id: cookie.euid },
      });

      if (resultData.status) {
        dispatch(setLoading(false));
        toast.success(resultData.message, {
          duration: 1000,
          style: confimationStyles,
        });
        handleResendCompleted();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prevResendTimer) => prevResendTimer - 1);
      }, 1000);
    } else {
      setShowResend(true);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [resendTimer]);

  useEffect(() => {
    localStorage.setItem("countdownStartTime", resendTimer.toString());
  }, [resendTimer]);

  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const handleSubmit = async (values) => {
    const setCookies = ({ token, name, role }) => {
      setCookie("e_t", token, { path: "/", expires: expirationTime() });
      setCookie("e_n", name, { path: "/", expires: expirationTime() });
      setCookie("e_r", role, { path: "/", expires: expirationTime() });
    };

    try {
      dispatch(setLoading(true));

      const dataToSend = { ...values, user_id: cookie.euid };
      const { data: resultData } = await postData({
        endpoint: "Auth/verifyOtp",
        data: {},
        params: {
          user_id: dataToSend.user_id,
          otp: dataToSend.otp,
          type: cookie.eut,
        },
      });

      dispatch(setLoading(false));

      if (resultData.status) {
        toast.success(resultData.message, {
          duration: 1000,
          style: confimationStyles,
        });

        dispatch(fetchAccessData(dataToSend.user_id));

        const requested = localStorage.getItem("requestedLocation");

        if (requested) {
          // to open the page that was requested before logging in
          const parsedLocation = JSON.parse(requested);


          if (parsedLocation?.pathname) {
            const fullPath = parsedLocation.search
              ? `${parsedLocation.pathname}${parsedLocation.search}`
              : parsedLocation.pathname;

            // Navigate to the correct path
            navigate(fullPath);
          } else {
            navigate("/dashboard");
          }

          localStorage.removeItem("requestedLocation"); 
        } else {
          navigate("/dashboard");
        }

        setCookies({
          token: resultData?.data?.token,
          name: btoa(resultData?.data?.user_name),
          role: btoa(resultData?.data?.role),
        });
        handleResendCompleted();
      } else {
        toast.error(resultData.message, { duration: 1000, style: errorStyles });
      }
    } catch (error) {
      dispatch(setLoading(false));
      console.error(error);
      toast.error("An error occurred. Please try again.", {
        duration: 1000,
        style: errorStyles,
      });
    }
  };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => handleSubmit(values)}
    >
      {({
        isValid,
        dirty,
        handleSubmit,
        isSubmitting,
        touched,
        errors,
        values,
        setFieldValue,
        handleBlur,
      }) => (
        <FormikForm onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Verification Code</label>
            <TextInput
              name="otp"
              type="text"
              className="primary-input"
              placeholder="Enter Verification Code"
              customholder="Enter Verification Code"
              autoComplete="off"
              maxLength={6}
              pattern="[0-9]*"
              onInput={(e) => {
                e.preventDefault();
                e.target.value = parseInt(e.target.value) || "";
                e.target.value = e.target.value.toString().slice(0, 6);
              }}
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  e.preventDefault();
                }
              }}
              icon={<Mail />}
              InputHeight="large"
            />
          </div>

          <button
            className="auth_btn btn-primary w-100 mt-4"
            disabled={isSubmitting}
            type="submit"
          >
            Verify{" "}
            {isSubmitting && (
              <span
                className="spinner-border spinner-border-sm ms-2"
                role="status"
                aria-hidden="true"
              ></span>
            )}
          </button>

          <div className={`form_footer mt-4`}>
            <button
              className="auth_btn btn-outline-primary"
              onClick={() => navigate(-1)}
              type="button"
            >
              Back
            </button>
            {resendTimer === 0 || showResend ? (
              <button
                className="auth_btn btn-outline-primary"
                onClick={handleResendClick}
                type="button"
                disabled={resendLoading} // Adjust the condition based on your logic
              >
                Resend
                {resendLoading && (
                  <span
                    className="spinner-border spinner-border-sm ms-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
              </button>
            ) : (
              <button
                className="auth_btn btn-outline-primary"
                onClick={handleResendClick}
                type="button"
                style={{ opacity: 0.7 }}
                disabled // Adjust the condition based on your logic
              >
                {formatTimer(resendTimer)}
              </button>
            )}
          </div>
        </FormikForm>
      )}
    </Formik>
  );
};

export default Form;
